import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  TrendingUp,
  Clock,
  AlertCircle,
  DollarSign,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
  CalendarPlus,
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts'
import { OrderCalendar } from '@/components/dashboard/OrderCalendar'
import { getOrders } from '@/api/orders'
import type { Order } from '@/api/orders'
import { driverApi } from '@/api/drivers'
import type { Driver } from '@/types'
import { useAuthStore } from '@/stores/authStore'

const statusConfig = {
  pending: { label: '待派單', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  in_transit: { label: '運送中', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
}

function KPICard({
  label,
  value,
  change,
  trend,
  icon: Icon,
  valueColor,
}: {
  label: string
  value: string
  change: string
  trend: 'up' | 'down' | 'neutral'
  icon: React.ElementType
  valueColor?: 'red' | 'default'
}) {
  const isPositive = trend === 'up'

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:border-slate-300 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-slate-600">
      {/* Gradient background on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-slate-800/50" />

      <div className="relative z-10 flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {label}
          </p>
          <p className={`mt-3 text-3xl font-bold ${valueColor === 'red' ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-50'}`}>
            {value}
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm font-medium">
            {isPositive && <ArrowUpRight className="h-4 w-4 text-green-600" />}
            {trend === 'down' && <ArrowDownRight className="h-4 w-4 text-red-600" />}
            <span
              className={isPositive ? 'text-green-600 dark:text-green-400' : 'text-slate-600 dark:text-slate-400'}
            >
              {change}
            </span>
          </div>
        </div>

        {/* Icon background */}
        <div className={`flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br transition-transform duration-300 group-hover:scale-110 ${valueColor === 'red' ? 'from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30' : 'from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800'}`}>
          <Icon className={`h-8 w-8 ${valueColor === 'red' ? 'text-red-500 dark:text-red-400' : 'text-slate-600 dark:text-slate-300'}`} />
        </div>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { user } = useAuthStore()
  const isDriver = user?.role === 'driver'
  const navigate = useNavigate()
  const calendarFirst = (() => {
    try {
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}')
      return !!tenant?.form_config?.dashboard_calendar_first
    } catch {
      return false
    }
  })()
  const showGoogleCalendarButton = (() => {
    try {
      const tenant = JSON.parse(localStorage.getItem('tenant') || '{}')
      return tenant?.form_config?.show_google_calendar_button ?? true
    } catch {
      return true
    }
  })()
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [drivers, setDrivers] = useState<Driver[]>([])
  const [loading, setLoading] = useState(true)
  const [chartView, setChartView] = useState<'week' | 'month'>(() => {
    // Load from localStorage on initial render
    const saved = localStorage.getItem('dashboardChartView')
    return (saved as 'week' | 'month') || 'month'
  })

  // Fetch orders and drivers from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [ordersRes, driversRes] = await Promise.all([
          getOrders({ limit: 500 }),
          driverApi.getDrivers(1, 100),
        ])
        setOrders(Array.isArray(ordersRes?.data) ? ordersRes.data : [])
        setDrivers(Array.isArray(driversRes?.items) ? driversRes.items : [])
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()

    const onVisible = () => { if (document.visibilityState === 'visible') fetchData() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  // Save chart view preference to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardChartView', chartView)
  }, [chartView])

  // 日曆目前顯示的月份：依月份另外向後端抓該月訂單，避免只靠最新 500 筆而看不到較早的月份
  const [calendarMonth, setCalendarMonth] = useState(() => new Date())
  const [calMonthOrders, setCalMonthOrders] = useState<Order[] | null>(null)

  useEffect(() => {
    let cancelled = false
    const y = calendarMonth.getFullYear()
    const m = calendarMonth.getMonth()
    const pad = (n: number) => String(n).padStart(2, '0')
    const last = new Date(y, m + 1, 0).getDate()
    getOrders({ limit: 1000, date_from: `${y}-${pad(m + 1)}-01`, date_to: `${y}-${pad(m + 1)}-${pad(last)}` })
      .then(res => { if (!cancelled) setCalMonthOrders(Array.isArray(res?.data) ? res.data : null) })
      .catch(() => { if (!cancelled) setCalMonthOrders(null) })
    return () => { cancelled = true }
  }, [calendarMonth.getFullYear(), calendarMonth.getMonth(), orders])

  // Convert API orders to calendar format
  const calendarOrders = (calMonthOrders ?? orders).map(order => {
    // Parse pickup_datetime - handle both formats
    let dateStr = ''
    let timeStr = ''
    
    if (order.pickup_datetime) {
      // If it's already in YYYY-MM-DD format
      if (order.pickup_datetime.includes('T')) {
        // ISO format - extract date and time
        const parts = order.pickup_datetime.split('T')
        dateStr = parts[0]
        timeStr = parts[1]?.substring(0, 5) || '00:00'
      } else if (order.pickup_datetime.includes(' ')) {
        // Space-separated format like "2025-02-13 14:30:00"
        const parts = order.pickup_datetime.split(' ')
        dateStr = parts[0]
        timeStr = parts[1]?.substring(0, 5) || '00:00'
      } else {
        // Already date only
        dateStr = order.pickup_datetime
        timeStr = '00:00'
      }
    }

    return {
      id: order.id,
      date: dateStr,
      customer: order.passenger_name,
      status: order.status as 'pending' | 'in_transit' | 'completed',
      fare: order.pricing.total_amount,
      time: timeStr,
    }
  })

  // Get orders for selected date
  const selectedDateOrders = selectedDate
    ? (calMonthOrders ?? orders).filter(order => {
        let orderDate = ''
        if (order.pickup_datetime) {
          if (order.pickup_datetime.includes('T')) {
            orderDate = order.pickup_datetime.split('T')[0]
          } else if (order.pickup_datetime.includes(' ')) {
            orderDate = order.pickup_datetime.split(' ')[0]
          } else {
            orderDate = order.pickup_datetime
          }
        }
        return orderDate === selectedDate
      })
    : []

  // Helper: extract date string from order datetime
  const getOrderDate = (dt: string) => {
    if (!dt) return ''
    if (dt.includes('T')) return dt.split('T')[0]
    if (dt.includes(' ')) return dt.split(' ')[0]
    return dt
  }

  // KPI計算（用本地時區日期，避免凌晨0-8點因UTC轉換算成前一天）
  const toLocalDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const today = toLocalDateStr(new Date())
  const todayOrders = orders.filter(o => getOrderDate(o.pickup_datetime) === today)
  const activeOrders = orders.filter(o => o.status === '進行中')
  const waitingOrders = orders.filter(o => o.status === '待派車')
  const availableDrivers = drivers.filter(d => d.status === 'available')
  const busyDrivers = drivers.filter(d => d.status === 'busy')
  const inspectionSoonDrivers = drivers.filter(d => {
    if (!d.vehicle_inspection_date) return false
    const inspDate = new Date(d.vehicle_inspection_date)
    const oneMonthLater = new Date()
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
    return inspDate <= oneMonthLater
  })
  const todayRevenue = todayOrders
    .filter(o => o.status === '已完成')
    .reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0)

  const now = new Date()
  const monthOrders = orders.filter(o => {
    const d = new Date(o.pickup_datetime)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && o.status === '已完成'
  })
  const monthRevenue = monthOrders.reduce((sum, o) => sum + (o.pricing?.total_amount || 0), 0)

  const driverKpiStats = [
    {
      label: '今日訂單',
      value: loading ? '-' : String(todayOrders.length),
      change: `待派 ${waitingOrders.length} 筆`,
      trend: 'neutral' as const,
      icon: Clock,
    },
    {
      label: '已完成',
      value: loading ? '-' : String(todayOrders.filter(o => o.status === '已完成').length),
      change: `待派車 ${waitingOrders.length}`,
      trend: activeOrders.length > 0 ? 'up' as const : 'neutral' as const,
      icon: AlertCircle,
    },
    {
      label: '今日收益',
      value: loading ? '-' : todayRevenue >= 1000
        ? `${Math.round(todayRevenue / 1000)}K`
        : String(Math.round(todayRevenue)),
      change: `共 ${todayOrders.filter(o => o.status === '已完成').length} 筆完成`,
      trend: todayRevenue > 0 ? 'up' as const : 'neutral' as const,
      icon: DollarSign,
    },
    {
      label: '本月收益',
      value: loading ? '-' : monthRevenue >= 1000
        ? `${Math.round(monthRevenue / 1000)}K`
        : String(Math.round(monthRevenue)),
      change: `共 ${monthOrders.length} 筆完成`,
      trend: monthRevenue > 0 ? 'up' as const : 'neutral' as const,
      icon: DollarSign,
    },
  ]

  const kpiStats = [
    {
      label: '今日訂單',
      value: loading ? '-' : String(todayOrders.length),
      change: `待派 ${waitingOrders.length} 筆`,
      trend: 'neutral' as const,
      icon: Clock,
    },
    {
      label: '本月收益',
      value: loading ? '-' : monthRevenue >= 1000
        ? `${Math.round(monthRevenue / 1000)}K`
        : String(Math.round(monthRevenue)),
      change: `共 ${monthOrders.length} 筆完成`,
      trend: monthRevenue > 0 ? 'up' as const : 'neutral' as const,
      icon: DollarSign,
    },
    {
      label: '驗車即到期',
      value: loading ? '-' : String(inspectionSoonDrivers.length),
      change: inspectionSoonDrivers.length > 0 ? '一個月內到期' : '無即將到期',
      trend: 'neutral' as const,
      icon: ShieldAlert,
      valueColor: inspectionSoonDrivers.length > 0 ? 'red' as const : 'default' as const,
    },
    {
      label: '今日收益',
      value: loading ? '-' : todayRevenue >= 1000
        ? `${Math.round(todayRevenue / 1000)}K`
        : String(Math.round(todayRevenue)),
      change: `共 ${todayOrders.filter(o => o.status === '已完成').length} 筆完成`,
      trend: todayRevenue > 0 ? 'up' as const : 'neutral' as const,
      icon: DollarSign,
    },
  ]

  // Calculate chart data
  const getChartData = () => {
    const data: Record<string, { date: string; orders: number; amount: number }> = {}
    const today = new Date()
    let startDate = new Date()

    if (chartView === 'week') {
      // Get last 7 days
      startDate.setDate(today.getDate() - 6)
    } else {
      // Get current month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1)
    }

    // Initialize data for each day
    for (let i = 0; i < (chartView === 'week' ? 7 : 31); i++) {
      const d = new Date(startDate)
      d.setDate(startDate.getDate() + i)
      const dateStr = toLocalDateStr(d)
      data[dateStr] = { date: dateStr, orders: 0, amount: 0 }
    }

    // Aggregate order data
    orders.forEach(order => {
      let orderDate = ''
      if (order.pickup_datetime) {
        if (order.pickup_datetime.includes('T')) {
          orderDate = order.pickup_datetime.split('T')[0]
        } else if (order.pickup_datetime.includes(' ')) {
          orderDate = order.pickup_datetime.split(' ')[0]
        }
      }

      if (data[orderDate]) {
        data[orderDate].orders += 1
        data[orderDate].amount += order.pricing.total_amount
      }
    })

    // Convert to array and format
    return Object.values(data).map(d => ({
      date: d.date.split('-')[2], // Show only day
      orders: d.orders,
      amount: Math.round(d.amount),
    }))
  }

  const chartData = getChartData()

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case '接機':
        return 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-700 dark:hover:bg-emerald-900/30'
      case '送機':
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/30'
      default:
        return 'bg-amber-50 border-amber-200 hover:bg-amber-100 dark:bg-amber-900/20 dark:border-amber-700 dark:hover:bg-amber-900/30'
    }
  }

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      pending: { label: '待派單', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      assigned: { label: '已指派', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
      picking_up: { label: '接客中', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      in_transit: { label: '運送中', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      completed: { label: '已完成', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      cancelled: { label: '已取消', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      no_show: { label: '未現身', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
    }
    return statusMap[status] || { label: status, color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400' }
  }

  const getGoogleCalendarButtonStyle = (category: string) => {
    switch (category) {
      case '接機':
        return 'bg-emerald-600 hover:bg-emerald-700 text-white'
      case '送機':
        return 'bg-blue-600 hover:bg-blue-700 text-white'
      default:
        return 'bg-amber-600 hover:bg-amber-700 text-white'
    }
  }

  const buildGoogleCalendarUrl = (order: Order): string => {
    const category = order.order_category
    const marker = category === '送機' ? '送' : category === '接機' ? '接' : category
    const title = `[${marker}] ${order.passenger_name} | ${order.passenger_phone}`

    const start = new Date(order.pickup_datetime.includes('T') ? order.pickup_datetime : order.pickup_datetime.replace(' ', 'T'))
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    const pad = (n: number) => String(n).padStart(2, '0')
    const fmt = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`

    const stopLines = [...(order.additional_stops || [])]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((s, i) => `中途點${i + 1}：${s.address}${s.name ? `（${s.name}${s.phone ? ' ' + s.phone : ''}）` : ''}`)

    const details = [
      `訂單編號：${order.order_number}`,
      `上車地點：${order.pickup_location}`,
      ...stopLines,
      `下車地點：${order.dropoff_location}`,
      order.flight_number ? `航班：${order.flight_number}` : '',
      `人數：${order.passenger_count}`,
      `行李：${order.luggage_count}`,
      order.child_seat ? `兒童座椅：${order.child_seat}` : '',
      order.booster_seat ? `增高墊：${order.booster_seat}` : '',
      order.name_board ? '需舉牌' : '',
      order.vehicle_type ? `車型：${order.vehicle_type}` : '',
      order.driver_name ? `司機：${order.driver_name}${order.driver_phone ? ' ' + order.driver_phone : ''}` : '',
      order.vehicle_number ? `車牌：${order.vehicle_number}` : '',
      `金額：$${Math.round(order.pricing?.total_amount || 0)}`,
      order.payment_method ? `付款方式：${order.payment_method}` : '',
      order.driver_notes ? `備註：${order.driver_notes}` : '',
    ].filter(Boolean).join('\n')

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${fmt(start)}/${fmt(end)}`,
      details,
      location: order.pickup_location || '',
    })
    return `https://calendar.google.com/calendar/render?${params.toString()}`
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* Header Section */}
      <div className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50">
        <div className="mx-auto max-w-7xl px-3 py-6 sm:px-4 md:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">
                儀錶板
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                實時車隊及訂單監控
              </p>
            </div>


          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-full px-3 py-8 sm:px-4 md:px-6 lg:px-8 flex flex-col">
        {/* KPI and Chart Section */}
        <div className={`mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6 ${calendarFirst ? 'order-2' : 'order-1'}`}>
          {/* Left: Chart Section */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/50 px-2 py-3">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">
                  接案統計
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setChartView('week')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      chartView === 'week'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    本週
                  </button>
                  <button
                    onClick={() => setChartView('month')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      chartView === 'month'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    本月
                  </button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 20, right: 0, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10 }}
                    width={35}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10 }}
                    width={40}
                  />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="orders"
                    fill="#3b82f6"
                    name="接案數"
                    barSize={60}
                    label={{ position: 'top', fontSize: 11, formatter: (value: number) => value === 0 ? '' : value }}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="amount"
                    fill="#10b981"
                    name="金額"
                    barSize={60}
                    label={{ position: 'top', fontSize: 11, formatter: (value: number) => value === 0 ? '' : value.toLocaleString() }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right: KPI Cards */}
          <div className="lg:col-span-1">
            <div className="grid gap-4 grid-cols-2">
              {(isDriver ? driverKpiStats : kpiStats).map((stat) => (
                <div key={stat.label} className="transform scale-90 origin-top-right">
                  <KPICard {...stat} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Calendar and Details Section (admin only) */}
        {!isDriver && <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${calendarFirst ? 'order-1 mb-8' : 'order-2'}`}>
          <div className="lg:col-span-2">
            <OrderCalendar
              orders={calendarOrders}
              onDateClick={setSelectedDate}
              selectedDate={selectedDate}
              onMonthChange={setCalendarMonth}
            />
          </div>

          {/* Order List */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/50 p-6">
            {selectedDate ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">
                    {selectedDate} 的訂單
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    共 {selectedDateOrders.length} 筆訂單
                  </p>
                </div>

                {/* Orders List */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : selectedDateOrders.length > 0 ? (
                    [...selectedDateOrders].sort((a, b) => {
                      const timeA = new Date(a.pickup_datetime).getTime()
                      const timeB = new Date(b.pickup_datetime).getTime()
                      return timeA - timeB
                    }).map(order => {
                      // Extract date and time from pickup_datetime
                      let displayDateTime = ''
                      if (order.pickup_datetime) {
                        if (order.pickup_datetime.includes('T')) {
                          const [date, time] = order.pickup_datetime.split('T')
                          displayDateTime = `${date} ${time.substring(0, 5)}`
                        } else if (order.pickup_datetime.includes(' ')) {
                          displayDateTime = order.pickup_datetime.substring(0, 16)
                        }
                      }
                      // Extract last 8 digits of order number
                      const orderNumberShort = `#${order.order_number.slice(-8)}`
                      
                      const isCompleted = order.status.includes('完成')
                      const hideDetail = isDriver && isCompleted
                      return (
                        <div
                          key={order.id}
                          className={`border rounded-lg p-3 transition-colors cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-offset-1 ${getCategoryStyle(order.order_category)}`}
                          onClick={() => navigate(`/orders?editOrder=${order.id}`)}
                          title="點擊開啟派車單"
                        >
                          {/* First row: Date & Time + Status Badge */}
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-medium text-sm text-slate-900 dark:text-slate-50">
                              {displayDateTime}
                            </p>
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold flex-shrink-0 ${
                              getStatusConfig(order.status).color
                            }`}>
                              {getStatusConfig(order.status).label}
                            </span>
                          </div>

                          {/* Second row: Passenger Name | Phone（已完成時司機不顯示） */}
                          {!hideDetail && (
                            <div className="flex items-center justify-between mb-2 text-xs">
                              <p className="font-medium text-slate-900 dark:text-slate-50">
                                {order.passenger_name}
                              </p>
                              <p className="text-slate-600 dark:text-slate-400">
                                {order.passenger_phone}
                              </p>
                            </div>
                          )}

                          {/* Third row: Category | Order Number */}
                          <div className="flex items-center justify-between mb-2 text-xs text-slate-600 dark:text-slate-400">
                            <p>{order.order_category}</p>
                            <p className="font-semibold text-slate-900 dark:text-slate-50">
                              {orderNumberShort}
                            </p>
                          </div>

                          {/* Driver Info */}
                          {!isDriver && (
                            <div className="mb-2 text-xs">
                              {order.driver_name ? (
                                <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                                  <span className="font-medium text-slate-900 dark:text-slate-50">
                                    {order.driver_name}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span>{order.driver_phone}</span>
                                    <span className="font-medium">{order.vehicle_number}</span>
                                    {order.vehicle_type && (
                                      <span className="inline-block px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                        {order.vehicle_type}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-slate-500 dark:text-slate-500 italic">未指派</p>
                              )}
                            </div>
                          )}

                          {/* Route & Amount（已完成時司機不顯示地址） */}
                          <div className="space-y-1 text-xs text-slate-600 dark:text-slate-400 pt-2 border-t border-black/10 dark:border-white/10">
                            {!hideDetail && (
                              <p>{order.pickup_location} → {order.dropoff_location}</p>
                            )}
                            <p className="font-semibold text-slate-900 dark:text-slate-50">
                              $ {Math.round(order.pricing.total_amount)}
                            </p>
                          </div>

                          {/* 加入 Google 日曆 */}
                          {!isDriver && showGoogleCalendarButton && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                window.open(buildGoogleCalendarUrl(order), '_blank', 'noopener,noreferrer')
                              }}
                              className={`mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${getGoogleCalendarButtonStyle(order.order_category)}`}
                            >
                              <CalendarPlus className="h-3.5 w-3.5" />
                              加入 Google 日曆
                            </button>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        該日期無訂單
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <Calendar className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-2" />
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  點擊左邊日期查看該天訂單
                </p>
              </div>
            )}
          </div>
        </div>}
      </div>
    </div>
  )
}
